import {Col, Row} from "reactstrap";
import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import {Paper} from "@mui/material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../components/app-specific/configurable-info-block";
import Api from "../../../../../../../core/services/api/api";

const SettingKeys = {
    codeStartingPoint: 'Code Starting Point',
}

const NCRSettingsConfigurationsView = () => {
    const [loadingConfigs, setLoadingConfigs] = useState(true);
    const [configs, _setConfigs] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Maps the key to ConfigurableBlockInputTypes so that the property input can be rendered
     * based on the property types.
     */
    const getInputType = useCallback((title) => {
        switch (title) {
            case SettingKeys.codeStartingPoint:
            default:
                return ConfigurableInfoBlockInputTypes.input;
        }
    }, [])

    /**
     * Maps the key to objects representing the properties that each of the types need.
     *
     */
    const getInputProps = useCallback((title, value) => {
        switch (title) {
            case SettingKeys.codeStartingPoint:
                return {
                    value: value,
                    type: "number",
                    placeholder: 'e.g. 123'
                };
            default:
                return {
                    value: value,
                    type: "text",
                    placeholder: 'e.g. text'
                };
        }
    }, [])

    /**
     * Sets the configs to the state.
     * @type {(function(*): void)|*}
     */
    const setConfigs = useCallback((configs) => {
        _setConfigs((prevState) =>
            Object
                .entries(typeof configs === 'function'
                    ? (configs(Object.fromEntries(prevState.map(e => [e.title, e.value]))) ?? {})
                    : (configs ?? {})
                )
                .map(([key, value]) => ({
                    title: key,
                    value: value,
                    inputType: getInputType(key),
                    inputProps: getInputProps(key, value),
                }))
        );
    }, [getInputProps, getInputType])

    /**
     * Fetches the settings from the api
     * @type {(function(): Promise<void>)}
     */
    const getSettings = useCallback(async () => {
        setLoadingConfigs(true);
        const response = await Api.getNcrPartnerSettings();
        if (!isMounted())
            return
        setLoadingConfigs(false);
        if (response?.resultFlag) {
            setConfigs(response.data);
        }
    }, [isMounted, setConfigs])


    /**
     * As soon as the component is mounted:
     * - fetches the settings from the api
     */
    useEffect(() => {
        void getSettings()
    }, [getSettings]);

    /**
     * Resets the settings to their default values.
     * @type {(function(): void)|*}
     */
    const resetToDefaultSettings = useCallback(() => {
        const _resetToDefaultSettings = async () => {
            setLoadingConfigs(true);
            const response = await Api.resetNcrPartnerSettingsToDefault();
            if (!isMounted())
                return
            setLoadingConfigs(false);
            if (response?.resultFlag) {
                setConfigs(response.data);
            }
        }
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to reset these settings to their default values?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && _resetToDefaultSettings(),
        );
    }, [isMounted, setConfigs])

    /**
     * Updates the config with the new value.
     * @type {(function(*, *): Promise<{resultFlag: boolean, message: string}|undefined|{resultFlag: boolean}>)}
     */
    const updateConfig = useCallback(async (config, value) => {
        if (typeof value === 'string' && value?.trim().length === 0)
            return {
                resultFlag: false,
                message: "Field cannot be empty"
            }
        const forApi = {
            name: config.title,
            value: value,
        }
        if (forApi.value === config.value)
            return;
        const response = await Api.updateNcrPartnerSettings(forApi)
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setConfigs(prevState => ({
                ...prevState,
                [config.title]: value,
            }))
        }
        return response;
    }, [isMounted, setConfigs])

    return (
        <>
            <Paper className={'paper'}>
                <Row noGutters className={'p-4 w-100'}>
                    <Col xs={true} className={'mb-3'}>
                        <p className={'view-title'}>
                            Configurations
                        </p>
                    </Col>
                    <Col xs={'auto'} className={'mb-3'}>
                        <Button
                            className={'w-100'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={resetToDefaultSettings}
                        >
                            Reset To Defaults
                        </Button>
                    </Col>
                    <Col xs={12}/>
                    {
                        loadingConfigs
                            ? Array(4).fill(null).map((_, e) => (
                                <Col key={e} xs={12} lg={6} xl={4} className={'loading normal mb-4 p-2'}>
                                    <div/>
                                </Col>
                            ))
                            : configs?.map((config) => (
                                <Col key={config.title} xs={12} lg={6} xl={4} className={'mb-4 p-2'}>
                                    <ConfigurableInfoBlock
                                        title={config.title ?? ''}
                                        inputType={config.inputType}
                                        inputProps={config.inputProps}
                                        onValueChanged={async (value) => updateConfig(config, value)}
                                    />
                                </Col>
                            ))
                    }
                </Row>
            </Paper>
        </>
    )
}

export default NCRSettingsConfigurationsView;
